/**
 * An extractor's values, each as a picture: what it holds as a bar across, what it takes in as a
 * speedometer, and the tiles it draws from as a little map in their shape on the planet.
 */
import { MACHINES, RULES } from '@bramvasterd/terrella-rules';
import type { JSX } from 'react';

import type { ExtractorInfo } from '@game/planet/machines';
import type { ReachTile } from '@game/planet/reach';
import { duration, kindName, resourceName, t } from '@i18n/index';

import { HoldsBar } from './HoldsBar';
import { IntakeGauge } from './IntakeGauge';
import { ReachMap } from './ReachMap';

export function ExtractorStats({
  info,
  reach,
}: {
  info: ExtractorInfo;
  reach: readonly ReachTile[];
}): JSX.Element {
  const resource = resourceName(info.resource).toLowerCase();
  const ground = kindName(MACHINES[info.type].ground).toLowerCase();
  const full = info.stored >= info.storage;
  const shared = reach.filter((tile) => tile.role === 'shared').length;
  // the sheet's colour, the machine's own (core/palette.ts), runs through its bar and its map
  return (
    <div className="xstats">
      <HoldsBar stored={info.stored} storage={info.storage} resource={resource} />
      <div className="stat-pair">
        <IntakeGauge rate={info.rate} most={info.ingest} period={duration(RULES.extractMs)} />
        <div className="reach" data-stat="reach">
          <span className="k">{t('info.reach')}</span>
          <ReachMap tiles={reach} self={reach.find((t) => t.role === 'self')?.tile ?? -1} />
          <b>{info.ground}</b>
          <small>
            {t('info.tiles', { n: info.ground, ground })}
            {shared > 0 && ` · ${t('info.shared-tiles', { n: shared })}`}
          </small>
        </div>
      </div>
      {full ? (
        <p className="note warn">{t('info.full')}</p>
      ) : (
        shared > 0 && <p className="note">{t('info.shared')}</p>
      )}
    </div>
  );
}
